import { ClickAwayListener } from "@nova/component-click-away-listener"
import { defaultTextToolContent } from "@nova/constants"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import classNames from "classnames"
import {
    selectIdRenaming,
    setIdRenaming,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { KeyboardEvent, useCallback, useEffect, useRef } from "react"

type InspectorPanelKeyValueLongTextProps = {
    id: string
    keyTitle: string
    value: string
    onConfirm: (input: string) => void
}

export const InspectorPanelKeyValueLongText = ({
    id,
    keyTitle,
    value,
    onConfirm,
}: InspectorPanelKeyValueLongTextProps) => {
    const dispatch = useAppDispatch()
    const idRenaming = useAppSelector(selectIdRenaming())
    const inputRef = useRef<HTMLHeadingElement>(null)
    const editedTextRef = useRef(value)

    const isRenaming = idRenaming === `inspector-panel-text-content-${id}`
    const isRenamingRef = useRef(isRenaming)

    /**
     * Auto-focus input field when idRenaming is changed to this input ID.
     *
     * Auto-confirm the content edits when idRenaming is changed FROM this input ID
     * to either a new ID or to undefined.
     */
    useEffect(() => {
        isRenamingRef.current = isRenaming

        if (isRenaming) inputRef.current?.focus()
        else {
            handleConfirmRef.current({ shouldClearIdRenaming: false })
        }

        return () => {
            if (isRenaming) handleConfirmRef.current({ shouldClearIdRenaming: false })
        }
    }, [isRenaming])

    /**
     * Confirm the input change when the user has made edits to the text
     * content but clicks away, where their click changes focus to a
     * different input field.
     */
    useEffect(() => {
        if (idRenaming && document.activeElement && document.activeElement !== inputRef.current)
            handleConfirmRef.current({ shouldClearIdRenaming: false })
    }, [idRenaming])

    /**
     * Populate input field on mount.
     */
    useEffectUnsafe(() => {
        if (!inputRef.current) return
        inputRef.current.innerText = value
    }, [])

    /**
     * Updates local state value of the text content, provided that the update
     * is different from the current text content value and the input area is
     * not focused. This prevents external overwriting of content that this
     * user is actively editing.
     */
    useEffect(() => {
        if (!inputRef.current) return
        if (value === inputRef.current.innerText) return
        if (document.activeElement === inputRef.current) return

        inputRef.current.innerText = value
    }, [value])

    const handleConfirm = useCallback(
        (args: { shouldClearIdRenaming: boolean } = { shouldClearIdRenaming: true }) => {
            if (args.shouldClearIdRenaming) dispatch(setIdRenaming(undefined))

            inputRef.current?.blur()

            let input = editedTextRef.current
            if (input.endsWith("\n")) input = input.slice(0, -1)

            // If no changes occurred.
            if (input === value) return

            onConfirm(input)
        },
        [onConfirm, dispatch, value]
    )
    const handleConfirmRef = useRef(handleConfirm)
    useEffect(() => {
        handleConfirmRef.current = handleConfirm
    }, [handleConfirm])

    const handleCancel = useCallback(() => {
        dispatch(setIdRenaming(undefined))

        if (inputRef.current) {
            inputRef.current?.blur()
            if (value !== inputRef.current.innerText) inputRef.current.innerText = value
        }
    }, [dispatch, value])

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case "Enter":
                    if (!e.shiftKey) {
                        e.stopPropagation()
                        handleConfirm()
                    }
                    break
                case "Escape":
                    e.stopPropagation()
                    handleCancel()
                    break
            }
        },
        [handleConfirm, handleCancel]
    )

    /**
     * Certain clipboard copies when pasted will paste an entire HTML element.
     * This handler will filter that out and paste just the raw text.
     */
    const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLHeadingElement>) => {
        e.preventDefault()

        const text = e.clipboardData?.getData("text/plain") ?? ""
        if (!text) return

        const selection = window.getSelection()
        if (!selection || !selection.rangeCount) return

        const range = selection.getRangeAt(0)
        range.deleteContents()
        const textNode = document.createTextNode(text)
        range.insertNode(textNode)
        range.setStartAfter(textNode)
        range.collapse(true)
        selection.removeAllRanges()
        selection.addRange(range)

        if (inputRef.current) editedTextRef.current = inputRef.current.innerText
    }, [])

    return (
        <div className="flex-none relative">
            {/* Key & RenamableText */}
            <div className="w-full mt-1.5">
                {/* Field Key */}
                <h5 className="text-gray-0 w-24 select-none flex-none">{keyTitle}</h5>

                {/* RenamableText */}
                <ClickAwayListener
                    onClickAway={() => {
                        if (isRenaming) handleConfirm()
                    }}
                >
                    <div className="flex-1 min-w-0">
                        {/* Editable H5 Element */}
                        {!!value || isRenaming ? (
                            <h5
                                ref={inputRef}
                                contentEditable
                                className={classNames(
                                    "outline-none resize-none overflow-hidden whitespace-pre-wrap break-words leading-tight min-w-[1ch] w-auto",
                                    "p-1.5 rounded-lg border cursor-text",
                                    {
                                        "text-gray-100 hover:text-gray-0 border-transparent":
                                            !isRenaming,
                                        "text-gray-0 border-white-300": isRenaming,
                                    }
                                )}
                                onClick={() =>
                                    dispatch(setIdRenaming(`inspector-panel-text-content-${id}`))
                                }
                                onKeyDown={handleKeyDown}
                                onPaste={handlePaste}
                                onInput={(e) => (editedTextRef.current = e.currentTarget.innerText)}
                            />
                        ) : (
                            // Placeholder "Add text" H5 Element
                            <h5
                                className="p-1.5 border border-transparent text-gray-100 hover:text-gray-0 cursor-text leading-tight"
                                onClick={() =>
                                    dispatch(setIdRenaming(`inspector-panel-text-content-${id}`))
                                }
                            >
                                {defaultTextToolContent}
                            </h5>
                        )}
                    </div>
                </ClickAwayListener>
            </div>
        </div>
    )
}
